import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { getJobProfile, putJobProfile } from "@/lib/jobsApi";
import ErrorState from "@/pages/jobs/ErrorState";
import DocumentLibrary from "@/pages/jobs/DocumentLibrary";

const RTW = [
  { v: "citizen", l: "NZ citizen" },
  { v: "resident", l: "NZ permanent resident" },
  { v: "visa", l: "Work visa" },
  { v: "other", l: "Other" },
];

export default function JobProfilePage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["jobs-profile"], queryFn: getJobProfile });
  const [form, setForm] = useState({});
  useEffect(() => {
    if (q.data?.profile) setForm(q.data.profile);
  }, [q.data]);

  const save = useMutation({
    mutationFn: () => {
      // Iter-263 v1 write-field boundary — send ONLY these keys plus
      // photo_data (when a new image was picked). Never echo server-owned
      // metadata like user_id/updated_at/photo_url back to the API.
      const editable = [
        "full_name",
        "phone",
        "email",
        "right_to_work_status",
        "headline",
        "location",
      ];
      const payload = {};
      for (const k of editable) {
        if (form[k] !== undefined) payload[k] = form[k];
      }
      if (form.photo_data) payload.photo_data = form.photo_data;
      return putJobProfile(payload);
    },
    onSuccess: (data) => {
      toast.success(data.profile_complete ? "Profile saved and complete." : "Profile saved.");
      qc.invalidateQueries({ queryKey: ["jobs-profile"] });
      // Iter-267 §3: apply-context returns `profile_complete` — refresh it
      // so the wizard's gate flips live after a photo is added.
      qc.invalidateQueries({ queryKey: ["apply-context"] });
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Save failed"),
  });

  const setPhoto = (file) => {
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, photo_data: reader.result }));
    reader.readAsDataURL(file);
  };

  if (q.isLoading) return <PageShell><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></PageShell>;
  if (q.isError) return <PageShell><ErrorState onRetry={() => q.refetch()} /></PageShell>;
  const complete = q.data?.profile_complete;
  return (
    <PageShell>
      <h1 className="font-display text-3xl font-black text-slate-900" data-testid="jobs-profile-title">
        Applicant profile
      </h1>
      <p className="text-sm text-slate-500 mt-1">
        Employers see this on every application. Fill it once, use it everywhere.
      </p>
      {!complete && (
        <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 text-sm" data-testid="profile-incomplete-banner">
          Complete your profile (name, phone, email, right-to-work AND a photo) before applying to a role.
        </div>
      )}
      <form
        onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
        className="mt-6 space-y-4"
        data-testid="jobs-profile-form"
        noValidate
      >
        <Row>
          <Field label="Full name *">
            <input data-testid="profile-full-name" className="input" value={form.full_name || ""} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
          </Field>
          <Field label="Phone *">
            <input data-testid="profile-phone" className="input" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </Field>
        </Row>
        <Row>
          <Field label="Email *">
            <input data-testid="profile-email" type="email" className="input" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>
          <Field label="Right to work *">
            <select data-testid="profile-rtw" className="input" value={form.right_to_work_status || ""} onChange={(e) => setForm({ ...form, right_to_work_status: e.target.value })}>
              <option value="">— pick one —</option>
              {RTW.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </Field>
        </Row>
        <Row>
          <Field label="Headline">
            <input data-testid="profile-headline" className="input" value={form.headline || ""} onChange={(e) => setForm({ ...form, headline: e.target.value })} placeholder="e.g. Senior finance ops with 8 years' Xero experience" />
          </Field>
          <Field label="Location">
            <input data-testid="profile-location" className="input" value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Auckland" />
          </Field>
        </Row>
        <Field label="Profile photo *">
          <div className="flex items-center gap-4">
            {(form.photo_data || form.photo_url) && (
              <img data-testid="profile-photo-preview" src={form.photo_data || form.photo_url} alt="" className="w-16 h-16 rounded-full object-cover ring-2 ring-slate-200" />
            )}
            <input data-testid="profile-photo-input" type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && setPhoto(e.target.files[0])} />
          </div>
        </Field>
        <button data-testid="profile-save-btn" disabled={save.isPending} className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold px-5 py-2.5 disabled:opacity-70">
          {save.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save profile
        </button>
      </form>
      {/* Iter-267 §2: Applicant profile and document library live on ONE
          screen, per the mobile pod's confirmed design. Upload/set-default/
          delete happens right here — no separate /jobs/documents page. */}
      <div id="docs">
        <DocumentLibrary
          heading="Resume & documents"
          subheading="Upload your CV and cover letter once — pick them from every application."
        />
      </div>
    </PageShell>
  );
}

function PageShell({ children }) {
  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-8 md:py-12">
      {children}
    </div>
  );
}
function Row({ children }) { return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>; }
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">{label}</span>
      {children}
    </label>
  );
}
